/**
 * VÉRAULT Member Suite — InvestmentBand (Wave 26.25 · Phase 3c).
 * ──────────────────────────────────────────────────────────────
 * Quiet editorial roll-up that sits above the day stack on the
 * Itinerary page. NOT a balance dashboard. Reads from
 *   GET /api/client-portal/experiences/{tripId}/cost-summary
 * and presents:
 *
 *   YOUR INVESTMENT · 5 of 25 items shared
 *
 *   Settled     $1,480   ─── editorial column trio
 *   Upcoming    $280
 *   Optional    $85
 *
 *   • Included · 4   • Paid · 2   • Complimentary · 1
 *
 * Silent when nothing is shared. Silent when the trip is veiled
 * (the page already gates that).
 */
import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { api } from "../../../../index";
import { CostStatusChip, formatAmount } from "./CostStatusChip";

const EASE_CALM = [0.22, 0.61, 0.36, 1];
const STATUS_ORDER = ["paid", "included", "complimentary", "deposit_due", "optional", "pending"];

export function InvestmentBand({ tripId }) {
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    if (!tripId) return undefined;
    let alive = true;
    api.get(`/client-portal/experiences/${tripId}/cost-summary`)
      .then(({ data }) => alive && setData(data))
      .catch(() => alive && setData(null));
    return () => { alive = false; };
  }, [tripId]);

  // Silent until we know there's something to show.
  if (!data) return null;
  // Wave 12.k — advisor master kill-switch (toggle lives in Money & Docs).
  if (data.band_visible_to_client === false) return null;
  const visible = data.items_visible_count || 0;
  const total = data.items_total_count || 0;
  if (visible === 0) return null;

  const settled = data.settled_total || 0;
  const upcoming = data.upcoming_total || 0;
  const optional = data.optional_total || 0;
  const currency = data.currency || "USD";

  // Columns to render — only show columns with a real amount.
  const columns = [];
  if (settled > 0) columns.push({ label: "Settled", value: settled, key: "settled" });
  if (upcoming > 0) columns.push({ label: "Upcoming", value: upcoming, key: "upcoming" });
  if (optional > 0) columns.push({ label: "Optional", value: optional, key: "optional" });

  // Status pills — render the chips with counts beside them, in our
  // chosen narrative order. Filters out 0-counts.
  const pills = STATUS_ORDER
    .map((s) => ({ status: s, count: (data.counts || {})[s] || 0 }))
    .filter((p) => p.count > 0);

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: EASE_CALM }}
      data-testid="investment-band"
      data-cost-currency={currency}
      data-cost-items-visible={visible}
      data-cost-items-total={total}
      className="border-y border-[rgba(0,0,0,0.06)] py-7 sm:py-9 space-y-6 sm:space-y-8"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          Your investment
        </p>
        <p className="font-accent italic text-[12px] sm:text-[13px] text-[hsl(var(--foreground-muted))]">
          {visible} of {total} item{total === 1 ? "" : "s"} shared
        </p>
      </div>

      {columns.length > 0 && (
        <div
          className={`grid gap-6 sm:gap-10 ${columns.length === 1 ? "grid-cols-1" : columns.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}
          data-testid="investment-band-columns"
        >
          {columns.map((c) => (
            <Link
              key={c.key}
              to={`/portal/client/experiences/${tripId}/essentials#balances`}
              data-testid={`investment-col-${c.key}`}
              data-essentials-anchor="balances"
              className="space-y-1.5 group block focus:outline-none"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] flex items-center gap-1.5">
                {c.label}
                <ChevronRight className="size-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" strokeWidth={1.5} />
              </p>
              <p className="font-serif text-[28px] sm:text-[34px] lg:text-[40px] tabular-nums leading-none tracking-tight group-hover:text-[hsl(var(--accent-gold))] transition-colors">
                {formatAmount(c.value, currency) || `${c.value}`}
              </p>
            </Link>
          ))}
        </div>
      )}

      {pills.length > 0 && (
        <div className="flex items-center gap-2.5 flex-wrap" data-testid="investment-band-pills">
          {pills.map((p) => (
            <span
              key={p.status}
              className="inline-flex items-center gap-2"
              data-testid={`investment-pill-${p.status}`}
            >
              <CostStatusChip status={p.status} />
              <span className="font-mono text-[10.5px] tracking-[0.18em] text-[hsl(var(--foreground-muted))] tabular-nums">
                · {p.count}
              </span>
            </span>
          ))}
        </div>
      )}
    </motion.section>
  );
}

export default InvestmentBand;
